import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { AlertTriangle, RefreshCw, Home, RotateCcw } from 'lucide-react';
import { captureError, setContext } from '@/lib/sentry';
import { createLogger } from '@/lib/logger';

const log = createLogger('ErrorBoundary');

type ErrorBoundaryVariant = 'page' | 'section' | 'widget';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Variant détermine le niveau de granularité de l'affichage */
  variant?: ErrorBoundaryVariant;
  /** Nom de la section pour le logging */
  sectionName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    const { sectionName, variant = 'page' } = this.props;
    const context = sectionName || variant;

    // Log error using centralized logger
    log.error(`Error in ${context}`, {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Send to Sentry in production
    setContext('react', {
      componentStack: errorInfo.componentStack,
      section: sectionName,
      variant,
    });
    captureError(error, {
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      section: sectionName,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  renderWidgetError() {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm">Erreur de chargement</span>
        <button
          onClick={this.handleRetry}
          className="ml-auto p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded"
          aria-label="Réessayer"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    );
  }

  renderSectionError() {
    const { sectionName } = this.props;
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-900 dark:text-red-100">
              {sectionName ? `Erreur dans ${sectionName}` : 'Erreur de section'}
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              Cette section n'a pas pu se charger correctement.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <p className="text-xs font-mono text-red-600 dark:text-red-400 mt-2 break-all">
                {this.state.error.message}
              </p>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleRetry}
              className="mt-3 text-red-700 border-red-300 hover:bg-red-100"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  renderPageError() {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 sm:p-8 text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>

          {/* Title */}
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Oups ! Une erreur est survenue
          </h1>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            L'application a rencontré un problème inattendu.
            Nous nous excusons pour ce désagrément.
          </p>

          {/* Error details (dev only) */}
          {import.meta.env.DEV && this.state.error && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-left">
              <p className="text-xs font-mono text-red-800 dark:text-red-300 break-all">
                {this.state.error.message}
              </p>
              {this.state.errorInfo && (
                <details className="mt-2">
                  <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer">
                    Voir la stack trace
                  </summary>
                  <pre className="mt-2 text-xs text-red-700 dark:text-red-300 overflow-auto max-h-40 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="outline"
              onClick={this.handleRetry}
              className="flex items-center justify-center gap-2"
            >
              <RefreshCw size={16} />
              Réessayer
            </Button>

            <Button
              onClick={this.handleGoHome}
              className="flex items-center justify-center gap-2"
            >
              <Home size={16} />
              Retour à l'accueil
            </Button>
          </div>

          {/* Refresh hint */}
          <p className="mt-6 text-xs text-gray-400">
            Si le problème persiste, essayez de rafraîchir la page
            ou contactez le support.
          </p>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { variant = 'page' } = this.props;

      switch (variant) {
        case 'widget':
          return this.renderWidgetError();
        case 'section':
          return this.renderSectionError();
        case 'page':
        default:
          return this.renderPageError();
      }
    }

    return this.props.children;
  }
}

// Hook for functional components to trigger error boundary
export function useErrorHandler() {
  return (error: Error) => {
    throw error;
  };
}

// Convenience components for different granularity levels
export function SectionErrorBoundary({
  children,
  name,
}: {
  children: ReactNode;
  name: string;
}) {
  return (
    <ErrorBoundary variant="section" sectionName={name}>
      {children}
    </ErrorBoundary>
  );
}

export function WidgetErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary variant="widget">
      {children}
    </ErrorBoundary>
  );
}
